<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup lang="ts">
import { ElEmpty } from 'element-plus'
import { useIntersectionObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { onMounted, watch } from 'vue'
import { useVisitsStore } from '~/stores/visits'
import VisitRow from '~/components/VisitRow.vue'

const visitsStore = useVisitsStore()
const { visits, loading, hasMore, selectedVisit } = storeToRefs(visitsStore)

// Watch for changes in visits array and select first item
watch(
  () => visits.value,
  (newVisits) => {
    if (newVisits.length > 0 && !selectedVisit.value) {
      visitsStore.selectVisit(newVisits[0])
    }
  },
  { immediate: true }
)

// Load initial data when component mounts
onMounted(async () => {
  await visitsStore.loadMore()
})

// Infinite scroll implementation
const loadingTrigger = ref<HTMLElement | null>(null)
useIntersectionObserver(
  loadingTrigger,
  ([{ isIntersecting }]) => {
    if (isIntersecting && !loading.value && hasMore.value) {
      visitsStore.loadMore()
    }
  },
  { threshold: 0.5 }
)

// Handle visit selection
const handleVisitClick = (visit: any) => {
  visitsStore.selectVisit(visit)
}
</script>

<template>
  <div v-loading="loading && !visits.length" class="visits-container bg-white">
    <ElEmpty
      v-if="!loading && !visits.length"
      description="No visits found!"
      :image-size="200"
    >
      <template #description>
        <p class="text-gray-500">
          No visits match your current search criteria
        </p>
      </template>
    </ElEmpty>

    <template v-else>
      <div class="visits-card" shadow="never">
        <VisitRow
          v-for="(visit, index) in visits"
          :key="visit.id"
          :visit="visit"
          :is-selected="selectedVisit?.id === visit.id"
          :index="index"
          :total-visits="visits.length"
          @select="handleVisitClick"
        />

        <!-- Loading trigger element -->
        <div
          v-if="hasMore"
          ref="loadingTrigger"
          class="h-10 flex items-center justify-center"
        >
          <div v-if="loading" class="text-gray-500 text-sm">
            <i class="animate-spin">⏳</i> Loading more visits...
          </div>
          <div v-else class="text-gray-400 text-xs">Scroll to load more</div>
        </div>

        <!-- End of results message -->
        <div
          v-if="!hasMore && visits.length > 0"
          class="h-10 flex items-center justify-center"
        >
          <div class="text-gray-400 text-xs">No more visits to load</div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.visits-container {
  height: 100%;
  overflow-y: auto;
}

.error-message {
  @apply p-4 text-red-500 text-center;
}

.visits-card {
  @apply bg-white;
}
</style>
